import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Lock, Star, Check, Moon } from "lucide-react";

const COLS = 4;          // stations per row on desktop
const ROW_H = 155;
const PAD = 46;

/** Winding treasure path: stations snake left→right, right→left, connected by a curved road. */
export const PathMap = ({ stages, restAfter, chestEvery = 10, onStart, cols = COLS }) => {
  const points = useMemo(() => stages.map((s, i) => {
    const row = Math.floor(i / cols);
    const inRow = i % cols;
    const leftToRight = row % 2 === 0;
    const step = (100 - PAD * 2 / 6) / (cols - 1);
    const xPct = PAD / 6 + (leftToRight ? inRow : cols - 1 - inRow) * step;
    const y = row * ROW_H + 64 + (inRow % 2 ? 18 : 0);
    return { ...s, x: xPct, y, labelAbove: inRow === cols - 1 };
  }), [stages, cols]);

  const height = Math.ceil(stages.length / cols) * ROW_H + 90;

  const d = points.reduce((acc, p, i) => {
    if (!i) return `M ${p.x} ${p.y}`;
    const prev = points[i - 1];
    const mx = (prev.x + p.x) / 2;
    return `${acc} C ${mx} ${prev.y}, ${mx} ${p.y}, ${p.x} ${p.y}`;
  }, "");

  return (
    <div className="relative mt-6 rounded-[28px] overflow-hidden border border-white/12"
      style={{
        background: "linear-gradient(180deg,#08183a 0%,#0b2350 55%,#08183a 100%)",
        minHeight: height,
      }} data-testid="journey-map">
      {/* soft hills + stars for depth */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background:
          "radial-gradient(600px 240px at 12% 12%, rgba(63,169,255,.20), transparent 65%)," +
          "radial-gradient(700px 300px at 88% 62%, rgba(255,180,63,.14), transparent 65%)," +
          "radial-gradient(500px 220px at 40% 100%, rgba(62,196,109,.14), transparent 70%)",
      }} />

      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" data-testid="journey-road"
        className="absolute inset-0 w-full" style={{ height }}>
        <path d={d} fill="none" stroke="rgba(255,255,255,.10)" strokeWidth="7"
          strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <path d={d} fill="none" stroke="rgba(255,180,63,.55)" strokeWidth="2.5"
          strokeDasharray="7 9" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      </svg>

      {points.map((p) => {
        const done = p.status === "done";
        const cur = p.status === "current";
        const restStop = p.index % restAfter === 0;
        const chestStop = p.index % chestEvery === 0;
        return (
          <div key={p.index} className="absolute -translate-x-1/2 flex flex-col items-center"
            style={{ left: `${p.x}%`, top: p.y - (p.labelAbove ? 52 : 26) }}>
            {p.labelAbove && (
              <div className="h-4 mb-1 flex items-center gap-[2px]">
                {done ? [1, 2, 3].map((n) => (
                  <Star key={n} size={9} fill={n <= p.stars ? "#ffb43f" : "transparent"}
                    style={{ color: "#ffb43f" }} />
                )) : <span className="text-[10px] text-white/45 flex items-center gap-1">
                  {restStop && <Moon size={9} />}{p.index}</span>}
              </div>
            )}
            <div className="relative flex flex-col items-center">
            <motion.button data-testid={`stage-${p.index}`} disabled={!cur}
              onClick={() => onStart(p.index)} title={p.topic_title_ar}
              whileHover={cur ? { scale: 1.08 } : {}}
              animate={cur ? { y: [0, -5, 0] } : {}}
              transition={cur ? { duration: 1.8, repeat: Infinity, ease: "easeInOut" } : {}}
              className="h-12 w-12 sm:h-14 sm:w-14 rounded-full grid place-items-center font-black
                text-white border-2 shadow-lg"
              style={{
                borderColor: cur ? "#ffb43f" : done ? "rgba(62,196,109,.7)" : "rgba(255,255,255,.18)",
                background: done ? "linear-gradient(180deg,#3ec46d,#1c8f4a)"
                  : cur ? "linear-gradient(180deg,#ffc861,#ef8a00)"
                    : "rgba(255,255,255,.07)",
                color: cur ? "#05122e" : "#fff",
                boxShadow: cur ? "0 0 0 6px rgba(255,180,63,.18)" : "none",
                opacity: p.status === "locked" ? 0.65 : 1,
              }}>
              {done ? <Check size={18} /> : p.status === "locked" ? <Lock size={14} /> : p.index}
            </motion.button>
            {chestStop && (
              <span className="absolute -top-3 -end-2 text-lg" title="صندوق مكافأة"
                data-testid={`chest-marker-${p.index}`}>🎁</span>
            )}
            </div>

            <div className="h-3 mt-1 flex items-center gap-[2px]">
              {!p.labelAbove && done && [1, 2, 3].map((n) => (
                <Star key={n} size={9} fill={n <= p.stars ? "#ffb43f" : "transparent"}
                  style={{ color: "#ffb43f" }} />
              ))}
              {!p.labelAbove && !done && <span className="text-[10px] text-white/45">{p.index}</span>}
            </div>
            {restStop && !p.labelAbove && (
              <span className="mt-[2px] text-[10px] text-white/50 flex items-center gap-1">
                <Moon size={9} />استراحة</span>
            )}
          </div>
        );
      })}
    </div>
  );
};
